import { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate, Link } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Mail, Phone, Smartphone, MapPin, RefreshCw, Upload, GitMerge, ChevronDown, ChevronRight, ChevronsUpDown, ChevronsDownUp, Building2, Settings } from "lucide-react";
import { toast } from "sonner";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { useConfirm } from "../components/ui/confirm-dialog";
import { phoneHref } from "../lib/formatters";
import { ContactsFilterButton, matchesFilters } from "../components/contacts/ContactsFilters";
import ContactFormDialog from "../components/contacts/ContactFormDialog";
import ImportContactsDialog from "../components/contacts/ImportContactsDialog";
import MergeContactsDialog from "../components/contacts/MergeContactsDialog";

const SYNC_BADGE = {
  synced: { label: "In Outlook", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  error: { label: "Sync failed", cls: "bg-red-50 text-red-700 border-red-200" },
  local: { label: "App only", cls: "bg-gray-100 text-gray-600 border-gray-200" },
};

const addressLine = (c) => [c.street && `${c.street}${c.apt ? ` ${c.apt}` : ""}`, [c.city, c.state].filter(Boolean).join(", "), c.zip_code].filter(Boolean).join(" · ");

const ContactRow = ({ c, onEdit, onDelete, onRetry, selected, onSelect }) => {
  const badge = SYNC_BADGE[c.sync_status] || SYNC_BADGE.pending;
  return (
    <div className={`grid grid-cols-1 md:grid-cols-[auto_1.4fr_1fr_1.6fr_auto] gap-3 py-3 border-b last:border-0 items-start ${selected ? "bg-sky-50/60" : ""}`} data-testid={`contact-row-${c.id}`}>
      <Checkbox checked={selected} onCheckedChange={() => onSelect(c.id)} className="mt-1" aria-label="Select for merge" data-testid={`contact-select-${c.id}`} />
      <div>
        <div className="font-medium" data-testid={`contact-name-${c.id}`}>{c.display_name}</div>
        <div className="text-xs text-muted-foreground">{[c.title, c.company].filter(Boolean).join(" · ")}</div>
        {c.notes && <div className="text-xs text-muted-foreground mt-1 italic line-clamp-2">{c.notes}</div>}
      </div>
      <div className="space-y-1 text-sm">
        {c.phone_office && <a href={phoneHref(c.phone_office)} className="flex items-center gap-1.5 text-sky-700 hover:underline"><Phone className="w-3.5 h-3.5" />{c.phone_office} <span className="text-xs text-muted-foreground">office</span></a>}
        {c.phone_mobile && <a href={phoneHref(c.phone_mobile)} className="flex items-center gap-1.5 text-sky-700 hover:underline"><Smartphone className="w-3.5 h-3.5" />{c.phone_mobile} <span className="text-xs text-muted-foreground">mobile</span></a>}
      </div>
      <div className="space-y-1 text-sm">
        {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-sky-700 hover:underline break-all"><Mail className="w-3.5 h-3.5 shrink-0" />{c.email}</a>}
        {addressLine(c) && <div className="flex items-center gap-1.5 text-muted-foreground text-xs"><MapPin className="w-3.5 h-3.5 shrink-0" />{addressLine(c)}</div>}
      </div>
      <div className="flex items-center gap-1 justify-end flex-wrap">
        <Badge variant="outline" className={`text-xs ${badge.cls}`} title={c.sync_error || (c.last_synced_at ? `Synced ${new Date(c.last_synced_at).toLocaleString()}` : "")} data-testid={`contact-sync-${c.id}`}>{badge.label}</Badge>
        {c.sync_status === "error" && <Button variant="ghost" size="icon" className="h-8 w-8" title={c.sync_error} onClick={() => onRetry(c)} data-testid={`contact-retry-${c.id}`}><RefreshCw className="w-4 h-4" /></Button>}
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(c)} title="Edit" data-testid={`contact-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => onDelete(c)} title="Delete" data-testid={`contact-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

const digits = (v) => (v || "").replace(/\D/g, "");
const GROUP_KEY = "v2r_contacts_group_by_company";

// Pairs sharing the same name or the same phone number.
const findDuplicatePairs = (contacts) => {
  const pairs = [];
  const seen = new Set();
  const byKey = {};
  for (const c of contacts) {
    const keys = [c.display_name && `n:${c.display_name.trim().toLowerCase()}`, digits(c.phone_office) && `p:${digits(c.phone_office)}`, digits(c.phone_mobile) && `p:${digits(c.phone_mobile)}`].filter(Boolean);
    for (const k of keys) {
      for (const other of byKey[k] || []) {
        const id = [other.id, c.id].sort().join("|");
        if (!seen.has(id)) { seen.add(id); pairs.push([other, c]); }
      }
      (byKey[k] = byKey[k] || []).push(c);
    }
  }
  return pairs;
};

const groupByCompany = (list) => {
  const groups = {};
  for (const c of list) (groups[(c.company || "").trim() || "No company"] = groups[(c.company || "").trim() || "No company"] || []).push(c);
  return Object.entries(groups).sort(([a], [b]) => (a === "No company") - (b === "No company") || a.localeCompare(b));
};

export default function ContactsPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [mergePair, setMergePair] = useState(null);
  const [grouped, setGrouped] = useState(() => localStorage.getItem(GROUP_KEY) === "1");
  const [collapsed, setCollapsed] = useState({});
  const toggleGrouped = (v) => { setGrouped(v); localStorage.setItem(GROUP_KEY, v ? "1" : "0"); };
  const onSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(-2)));
  const [notDups, setNotDups] = useState([]);
  const pairKey = (a, b) => [a.id, b.id].sort().join("|");
  const dupPairs = useMemo(() => findDuplicatePairs(contacts).filter(([a, b]) => !notDups.includes(pairKey(a, b))), [contacts, notDups]);
  const markNotDuplicate = async ([a, b]) => {
    try {
      await apiClient.post("/shared-contacts/not-duplicates", { a_id: a.id, b_id: b.id });
      setNotDups((prev) => [...prev, pairKey(a, b)]);
      toast.success(`${a.display_name} and ${b.display_name} marked as different people`);
    } catch { toast.error("Could not save"); }
  };
  const [showDismissed, setShowDismissed] = useState(false);
  const restorePair = async (key) => {
    const [aId, bId] = key.split("|");
    try {
      await apiClient.delete(`/shared-contacts/not-duplicates/${aId}/${bId}`);
      setNotDups((prev) => prev.filter((k) => k !== key));
      toast.success("Pair restored to duplicate checking");
    } catch { toast.error("Could not restore"); }
  };
  const byId = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c])), [contacts]);
  const openMerge = (pair) => { setMergePair(pair); };

  const load = useCallback(async () => {
    try {
      const [list, nd] = await Promise.all([apiClient.get("/shared-contacts"), apiClient.get("/shared-contacts/not-duplicates")]);
      setContacts(list.data); setNotDups(nd.data || []); setSelected([]);
    }
    catch { toast.error("Could not load contacts"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [filters, setFilters] = useState({ position: "", company: "" });
  const hasFilters = !!(filters.position || filters.company);
  const byName = (a, b) => (a.display_name || "").localeCompare(b.display_name || "", undefined, { sensitivity: "base" });
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = contacts.filter((c) => matchesFilters(c, filters) && (!q || [c.display_name, c.company, c.title, c.email, c.phone_office, c.phone_mobile, c.city, c.notes].some((v) => (v || "").toLowerCase().includes(q))));
    return [...base].sort(byName);
  }, [contacts, search, filters]);
  const groups = useMemo(() => (grouped ? groupByCompany(filtered) : []), [grouped, filtered]);
  const setAllCollapsed = (v) => setCollapsed(Object.fromEntries(groups.map(([name]) => [name, v])));

  const onDelete = async (c) => {
    const ok = await confirm({ title: `Delete ${c.display_name}?`, description: c.exo_guid ? "This also removes the contact from everyone's Outlook address list." : "This removes the contact from the app.", confirmText: "Delete", confirmVariant: "destructive" });
    if (!ok) return;
    try {
      const r = (await apiClient.delete(`/shared-contacts/${c.id}`)).data;
      toast.success(r.removed_from_exchange ? "Contact deleted" : "Contact deleted — Outlook removal will retry at the next sync");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const onRetry = async (c) => {
    try {
      const r = (await apiClient.post(`/shared-contacts/${c.id}/sync`)).data;
      r.ok ? toast.success("Synced to Outlook") : toast.error(r.error || "Sync failed", { duration: 10000 });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Sync failed"); }
  };

  if (!["admin", "project_manager", "office_manager"].includes(user?.role)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="page-enter space-y-6" data-testid="contacts-page">
      <div className="page-header flex flex-row items-center justify-between gap-3">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground whitespace-nowrap">Contacts</h1>
        <div className="flex items-center gap-2 shrink-0 page-header-actions">
          <Button className="h-9 w-9 p-0 lg:h-10 lg:w-auto lg:px-4" onClick={() => { setEditing(null); setDialogOpen(true); }} aria-label="Add Contact" data-testid="add-shared-contact-btn"><Plus className="w-4 h-4 lg:mr-2" /><span className="hidden lg:inline">Add Contact</span></Button>
          <Button variant="outline" className="hidden lg:inline-flex" onClick={() => setImportOpen(true)} data-testid="import-contacts-btn"><Upload className="w-4 h-4 mr-2" /> Import</Button>
          <Button variant="ghost" asChild className="hidden lg:inline-flex" data-testid="contact-settings-link"><Link to="/settings?tab=contacts"><Settings className="w-4 h-4 mr-2" /> Sync settings</Link></Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company, email, phone…" className="pl-9" data-testid="contacts-search" />
            </div>
            {grouped && groups.length > 0 && (
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setAllCollapsed(false)} data-testid="expand-all-btn"><ChevronsUpDown className="w-3.5 h-3.5 mr-1" /> Expand all</Button>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setAllCollapsed(true)} data-testid="collapse-all-btn"><ChevronsDownUp className="w-3.5 h-3.5 mr-1" /> Collapse all</Button>
              </div>
            )}
            <ContactsFilterButton contacts={contacts} filters={filters} onChange={setFilters} search={search} onSearch={setSearch} />
            <Button size="sm" variant={selected.length === 2 ? "default" : "outline"} disabled={selected.length !== 2} onClick={() => openMerge(selected.map((id) => byId[id]))} data-testid="merge-selected-btn">
              <GitMerge className="w-4 h-4 mr-1" /> Merge {selected.length ? `(${selected.length}/2)` : "2"}
            </Button>
            <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto">
              <Switch checked={grouped} onCheckedChange={toggleGrouped} data-testid="group-by-company-switch" />
              <Label className="cursor-pointer flex items-center gap-1"><Building2 className="w-4 h-4" /> Group by company</Label>
            </label>
          </div>
          {dupPairs.length > 0 && !search && !hasFilters && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 mb-3 text-xs text-amber-900 space-y-1" data-testid="duplicates-hint">
              <div className="font-medium">Possible duplicates ({dupPairs.length})</div>
              {dupPairs.slice(0, 5).map(([a, b]) => (
                <div key={`${a.id}|${b.id}`} className="flex flex-wrap items-center gap-2">
                  <span>{a.display_name} ↔ {b.display_name}</span>
                  <button type="button" className="underline text-amber-800 hover:text-amber-950" onClick={() => openMerge([a, b])} data-testid={`dup-merge-${a.id}-${b.id}`}>Review & merge</button>
                  <span className="text-amber-400">·</span>
                  <button type="button" className="underline text-amber-800 hover:text-amber-950" onClick={() => markNotDuplicate([a, b])} data-testid={`dup-ignore-${a.id}-${b.id}`}>Not a duplicate</button>
                </div>
              ))}
            </div>
          )}
          {!search && !hasFilters && notDups.some((k) => k.split("|").every((id) => byId[id])) && (
            <div className="mb-3 text-xs" data-testid="dismissed-pairs">
              <button type="button" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground underline" onClick={() => setShowDismissed((v) => !v)} data-testid="dismissed-pairs-toggle">
                {showDismissed ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Dismissed pairs ({notDups.filter((k) => k.split("|").every((id) => byId[id])).length}) — marked "not a duplicate"
              </button>
              {showDismissed && (
                <div className="mt-1 rounded-md border border-border bg-muted/40 px-3 py-2 space-y-1">
                  {notDups.filter((k) => k.split("|").every((id) => byId[id])).map((k) => {
                    const [a, b] = k.split("|").map((id) => byId[id]);
                    return (
                      <div key={k} className="flex flex-wrap items-center gap-2" data-testid={`dismissed-pair-${k.replace("|", "-")}`}>
                        <span>{a.display_name} ↔ {b.display_name}</span>
                        <button type="button" className="underline text-sky-700 hover:text-sky-900" onClick={() => restorePair(k)} data-testid={`dismissed-restore-${k.replace("|", "-")}`}>Restore</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {loading ? <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="contacts-empty">{contacts.length === 0 ? "No contacts yet. Add the people you work with — they'll show up in everyone's Outlook." : hasFilters && !search ? "No contacts match these filters." : "No contacts match your search."}</p>
            ) : (
              <div data-testid="contacts-list">
                <div className="text-xs text-muted-foreground pb-1 flex items-center gap-2">{filtered.length} contact{filtered.length === 1 ? "" : "s"}
                  {hasFilters && <button type="button" className="underline hover:text-foreground" onClick={() => setFilters({ position: "", company: "" })} data-testid="clear-filters-inline">clear filters</button>}
                </div>
                {grouped ? groups.map(([company, list]) => (
                  <div key={company} data-testid={`company-group-${company.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [company]: !c[company] }))} className="w-full flex items-center gap-2 py-2 mt-2 text-sm font-semibold border-b hover:bg-accent/40 rounded-t" data-testid={`company-group-toggle-${company.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                      {collapsed[company] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      <Building2 className="w-4 h-4 text-muted-foreground" /> {company} <span className="text-muted-foreground font-normal">({list.length})</span>
                    </button>
                    {!collapsed[company] && list.map((c) => <ContactRow key={c.id} c={c} selected={selected.includes(c.id)} onSelect={onSelect} onEdit={(x) => { setEditing(x); setDialogOpen(true); }} onDelete={onDelete} onRetry={onRetry} />)}
                  </div>
                )) : filtered.map((c) => <ContactRow key={c.id} c={c} selected={selected.includes(c.id)} onSelect={onSelect} onEdit={(x) => { setEditing(x); setDialogOpen(true); }} onDelete={onDelete} onRetry={onRetry} />)}
              </div>
            )}
        </CardContent>
      </Card>

      <ContactFormDialog open={dialogOpen} onOpenChange={setDialogOpen} contact={editing} onSaved={load} />
      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => setTimeout(load, 1500)} />
      <MergeContactsDialog open={!!mergePair} onOpenChange={(o) => { if (!o) setMergePair(null); }} pair={mergePair} onMerged={() => setTimeout(load, 800)} />
    </div>
  );
}
